// Crowd-sourced deals pool: clients fetch random seeds from the server,
// fall back to local generation when offline, and push successful local
// generations back to the server so the pool grows organically.

import type { DrawMode } from "../game/types.ts";
import { getSupabase } from "./supabase.ts";

const CACHE_KEY = "just-solitaire:v1:deals-cache";
const RECENT_KEY = "just-solitaire:v1:recent-deals";
const CLEARED_KEY = "just-solitaire:v1:cleared-deals";
const TARGET_CACHE_SIZE = 8;
const REFILL_THRESHOLD = 3;
const RECENT_HISTORY_SIZE = 24;
const CLEARED_HISTORY_SIZE = 500;

interface DealCache {
  one: string[];
  three: string[];
}

interface RecentDeals {
  one: string[];
  three: string[];
}

function emptyByMode(): DealCache {
  return { one: [], three: [] };
}

function normalizeSeedList(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  const out: string[] = [];
  const seen = new Set<string>();
  for (const item of value) {
    let seed: unknown = item;
    if (item && typeof item === "object") {
      seed = (item as { seed?: unknown; get_random_deals?: unknown }).seed
        ?? (item as { get_random_deals?: unknown }).get_random_deals;
    }
    if (typeof seed !== "string" || seed.length === 0 || seen.has(seed)) continue;
    seen.add(seed);
    out.push(seed);
  }
  return out;
}

function loadCache(): DealCache {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return emptyByMode();
    const parsed = JSON.parse(raw);
    return {
      one: Array.isArray(parsed.one) ? parsed.one : [],
      three: Array.isArray(parsed.three) ? parsed.three : [],
    };
  } catch {
    return emptyByMode();
  }
}

function saveCache(cache: DealCache): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
  } catch {
    // ignore quota errors
  }
}

function loadRecent(): RecentDeals {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    if (!raw) return emptyByMode();
    const parsed = JSON.parse(raw);
    return {
      one: Array.isArray(parsed.one) ? parsed.one : [],
      three: Array.isArray(parsed.three) ? parsed.three : [],
    };
  } catch {
    return emptyByMode();
  }
}

function saveRecent(recent: RecentDeals): void {
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(recent));
  } catch {
    // ignore quota errors
  }
}

function loadCleared(): RecentDeals {
  try {
    const raw = localStorage.getItem(CLEARED_KEY);
    if (!raw) return emptyByMode();
    const parsed = JSON.parse(raw);
    return {
      one: Array.isArray(parsed.one) ? parsed.one : [],
      three: Array.isArray(parsed.three) ? parsed.three : [],
    };
  } catch {
    return emptyByMode();
  }
}

function saveCleared(cleared: RecentDeals): void {
  try {
    localStorage.setItem(CLEARED_KEY, JSON.stringify(cleared));
  } catch {
    // ignore quota errors
  }
}

function blockedSeeds(mode: DrawMode): Set<string> {
  return new Set([...loadRecent()[mode], ...loadCleared()[mode]]);
}

function removeFromCache(mode: DrawMode, seed: string): void {
  const cache = loadCache();
  cache[mode] = cache[mode].filter((s) => s !== seed);
  saveCache(cache);
}

export function rememberPlayedSeed(mode: DrawMode, seed: string): void {
  const recent = loadRecent();
  recent[mode] = [seed, ...recent[mode].filter((s) => s !== seed)].slice(0, RECENT_HISTORY_SIZE);
  saveRecent(recent);
}

export function rememberClearedSeed(mode: DrawMode, seed: string): void {
  rememberPlayedSeed(mode, seed);
  const cleared = loadCleared();
  cleared[mode] = [seed, ...cleared[mode].filter((s) => s !== seed)]
    .slice(0, CLEARED_HISTORY_SIZE);
  saveCleared(cleared);
  removeFromCache(mode, seed);
}

// Pull one seed off the local cache for a given mode. Returns null if empty.
export function popCachedSeed(mode: DrawMode): string | null {
  const cache = loadCache();
  const list = cache[mode];
  const blocked = blockedSeeds(mode);
  while (list.length > 0) {
    const seed = list.shift();
    if (seed && !blocked.has(seed)) {
      saveCache(cache);
      return seed;
    }
  }
  saveCache(cache);
  return null;
}

// Fetch fresh seeds from the server and add to cache (best effort).
// Returns the number of seeds actually added.
export async function refillCache(mode: DrawMode): Promise<number> {
  const sb = getSupabase();
  if (!sb) return 0;
  const cache = loadCache();
  const want = TARGET_CACHE_SIZE - cache[mode].length;
  if (want <= 0) return 0;
  const blocked = blockedSeeds(mode);
  const existing = new Set(cache[mode]);

  const fetchCount = Math.min(128, want + blocked.size + TARGET_CACHE_SIZE);
  const { data, error } = await sb.rpc("get_random_deals", { mode, n: fetchCount });
  if (error || !data) return 0;
  const seeds = normalizeSeedList(data)
    .filter((seed) => !blocked.has(seed) && !existing.has(seed))
    .slice(0, want);
  if (seeds.length === 0) return 0;
  cache[mode].push(...seeds);
  saveCache(cache);
  return seeds.length;
}

// Decide if we should kick off a background refill.
export function cacheNeedsRefill(mode: DrawMode): boolean {
  const cache = loadCache();
  return cache[mode].length <= REFILL_THRESHOLD;
}

// Push a locally-generated seed to the server pool. Best-effort.
export async function pushSeed(seed: string, mode: DrawMode): Promise<void> {
  const sb = getSupabase();
  if (!sb) return;
  const { data: sess } = await sb.auth.getSession();
  const uid = sess.session?.user.id;
  if (!uid) return;
  const { error } = await sb
    .from("deals")
    .upsert(
      { seed, draw_mode: mode, submitted_by: uid },
      { onConflict: "seed", ignoreDuplicates: true },
    );
  if (error) {
    console.warn("[pool] pushSeed failed:", error.message);
  }
}

export interface WorldRecord {
  wrTime: number | null;
  wrMoves: number | null;
  playersCleared: number;
}

// Fetch the world record for a specific seed. Returns null if the row doesn't
// exist (e.g., seed never pushed) or the network request fails.
export async function fetchWorldRecord(seed: string): Promise<WorldRecord | null> {
  const sb = getSupabase();
  if (!sb) return null;
  const { data, error } = await sb
    .from("deal_world_records")
    .select("wr_time_sec, wr_moves, players_cleared")
    .eq("seed", seed)
    .maybeSingle();
  if (error || !data) return null;
  return {
    wrTime: (data.wr_time_sec as number | null) ?? null,
    wrMoves: (data.wr_moves as number | null) ?? null,
    playersCleared: Number(data.players_cleared ?? 0),
  };
}

// Record a clear on the server (best stats are kept atomically).
export async function recordDealClear(
  seed: string,
  timeSec: number,
  moves: number,
): Promise<void> {
  const sb = getSupabase();
  if (!sb) return;
  const { error } = await sb.rpc("record_deal_clear", {
    p_seed: seed,
    p_time_sec: timeSec,
    p_moves: moves,
  });
  if (error) console.warn("[pool] recordDealClear failed:", error.message);
}
